import { useMemo, useRef, useState } from 'react';

import { Address } from '@flipster/apis/v1/support/types';
import { useRawSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { FormattedMessage } from '@flipster/i18n';
import { NoData } from '@flipster/react/design-system-legacy/no-data';
import { Table } from '@flipster/react/design-system-legacy/table';
import { Spinner } from '@flipster/react/design-system/spinner';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { NavigationHeightVariableKey } from '@flipster/react/panda/globals/vars';
import { css } from '@flipster/styled-system/css';
import { center, circle, hstack, stack } from '@flipster/styled-system/patterns';

import { Navigator } from './navigator';

interface Props {
  addresses?: Address[];
}

export const AddressList = ({ addresses }: Props) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState<number>(0);

  const { breakpoint, isMobile } = useBreakpoint();
  const { currenciesKeyByCurrency } = useRawSnapshotSpec();

  const addressItems = useMemo(
    () =>
      addresses?.reduce(
        (acc: Array<Address & { icon: string; addressExternalUrls?: { address: string; href: string }[] }>, cur) => {
          if (!cur.address) return acc;
          const currentCurrency = currenciesKeyByCurrency?.[cur.currency];
          const blockchainInfo = currentCurrency?.blockchainInfo?.find((b) => b.blockchainName === cur.network);
          if (!currentCurrency || !blockchainInfo) return acc;
          const explorerLink = blockchainInfo.addressExplorerLink;
          // TODO - 서버에서 string 으로 타입이 넘어와서 임시 \n 로 파싱 array 타입은 변경하기로 함
          // https://prextech.slack.com/archives/C02MD64EYUC/p1719826931889129?thread_ts=1719637918.203399&cid=C02MD64EYUC
          acc.push({
            ...cur,
            icon: currentCurrency.icon,
            currency: currentCurrency.currency,
            network: blockchainInfo.networkName,
            addressExternalUrls: cur.address.split('\n').map((address) => ({
              address,
              href: explorerLink.replace('${address}', address),
            })),
          });
          return acc;
        },
        [],
      ),
    [addresses, currenciesKeyByCurrency],
  );

  const PagePerItem = 10;
  const rows = (addressItems ?? []).slice(page * PagePerItem, (page + 1) * PagePerItem);

  return (
    <div style={{ scrollMarginTop: `var(${NavigationHeightVariableKey})` }} ref={listRef}>
      <h2 className={css({ textStyle: 'Title/S', color: 'content.default.level-5', mb: { base: 24, tablet: 38 } })}>
        <FormattedMessage id='Autogen_common.r251126.s_Wallet_address' />
      </h2>
      {(() => {
        if (!breakpoint)
          return (
            <div className={center({ w: '100%', py: 120 })}>
              <Spinner size={24} />
            </div>
          );
        if (!addressItems?.length) return <NoData />;
        if (isMobile)
          return (
            <ul>
              {rows.map((row) => (
                <li
                  key={`li-${row.currency}-${row.network}`}
                  className={css({
                    borderBottomWidth: '1px',
                    borderBottomStyle: 'solid',
                    borderColor: 'border.default.level-1',
                  })}
                >
                  <details>
                    <summary className={hstack({ gap: 8, py: 20 })}>
                      <div className={hstack({ justify: 'space-between', gap: 8, flex: 1 })}>
                        <div className={hstack({ textStyle: 'Text/M/Strong', gap: 6 })}>
                          <img src={row.icon} alt={row.currency} className={circle({ size: 20 })} />
                          {row.currency}
                        </div>
                        <Formatted.Number
                          value={row.balance}
                          suffix={row.currency}
                          css={{ textStyle: 'Text/M/Monospace/Regular' }}
                        />
                      </div>
                      <ChevronDownIcon size={16} className={css({ '[open] &': { transform: 'rotate(180deg)' } })} />
                    </summary>
                    <dl
                      className={stack({
                        textStyle: 'Text/M/Regular',
                        gap: 12,
                        pb: 20,
                        '& > div': hstack.raw({ justify: 'space-between', gap: 8 }),
                        '& dt': { color: 'content.default.level-2' },
                        '& dd': { flexBasis: '60%', textAlign: 'right' },
                      })}
                    >
                      <div>
                        <dt>
                          <FormattedMessage id='Autogen_common.r251126.s_Network' />
                        </dt>
                        <dd>{row.network}</dd>
                      </div>
                      {row.addressExternalUrls && (
                        <div>
                          <dt>
                            <FormattedMessage id='Autogen_common.r251126.s_Address' />
                          </dt>
                          <dd className={css({ overflow: 'auto' })}>
                            {row.addressExternalUrls.map(({ href, address }) => (
                              <a
                                href={href}
                                key={href}
                                className={addressLinkStyle}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                {address}
                              </a>
                            ))}
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt>
                          <FormattedMessage id='Autogen_common.r251126.s_Block_Height' />
                        </dt>
                        <dd>{row.blockHeight}</dd>
                      </div>
                    </dl>
                  </details>
                </li>
              ))}
            </ul>
          );
        if (!isMobile)
          return (
            <Table>
              <Table.Header>
                <Table.Row css={{ textStyle: 'Text/M/Strong' }}>
                  <Table.HeaderCell w={120}>
                    <FormattedMessage id='Autogen_common.r251126.s_Asset' />
                  </Table.HeaderCell>
                  <Table.HeaderCell w={200}>
                    <FormattedMessage id='Autogen_common.r251126.s_Network' />
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    <FormattedMessage id='Autogen_common.r251126.s_Address' />
                  </Table.HeaderCell>
                  <Table.HeaderCell w={200} justify='end'>
                    <FormattedMessage id='Autogen_common.r251126.s_Balance' />
                  </Table.HeaderCell>
                  <Table.HeaderCell w={110} justify='end'>
                    <FormattedMessage id='Autogen_common.r251126.s_Block_Height' />
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body css={{ textStyle: 'Text/M/Regular' }}>
                {rows.map((row) => (
                  <Table.Row key={`table-${row.currency}-${row.network}`}>
                    <Table.BodyCell css={{ textStyle: 'Text/M/Strong' }}>
                      <div className={hstack({ gap: 6 })}>
                        <img src={row.icon} alt={row.currency} className={circle({ size: 20 })} />
                        {row.currency}
                      </div>
                    </Table.BodyCell>
                    <Table.BodyCell>{row.network}</Table.BodyCell>
                    <Table.BodyCell>
                      {row.addressExternalUrls?.map(({ href, address }) => (
                        <a
                          href={href}
                          key={href}
                          className={addressLinkStyle}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {address}
                        </a>
                      ))}
                    </Table.BodyCell>
                    <Table.BodyCell justify='end' css={{ textStyle: 'Text/M/Monospace/Regular' }}>
                      <Formatted.Number value={row.balance} />
                    </Table.BodyCell>
                    <Table.BodyCell justify='end' css={{ textStyle: 'Text/M/Monospace/Regular' }}>
                      {row.blockHeight}
                    </Table.BodyCell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          );
        return null;
      })()}
      <Navigator
        current={page}
        onChange={(p) => {
          setPage(p);
          if (isMobile) listRef.current?.scrollIntoView({ block: 'start', inline: 'start' });
        }}
        per={PagePerItem}
        total={addressItems?.length || 0}
        css={{ mt: 12 }}
      />
    </div>
  );
};

const addressLinkStyle = css({
  display: 'block',
  textStyle: 'Text/M/Monospace/Strong',
  color: 'content.default.level-5',
  overflowWrap: 'anywhere',
  textDecoration: 'underline',
  '&:hover': {
    color: 'content.default.level-2',
  },
});
